(ns storrito.instagram.proxy-error
  "User-friendly error messages for Instagram requests that fail because a
   proxy or the Storrito Relay App is not reachable."
  (:require [clojure.string :as str]))

(defn relay-app-proxy?
  [proxy-url]
  (and (string? proxy-url)
       (or (str/includes? proxy-url "@relay:9000")
           (str/includes? proxy-url "proxy.storrito.com:9000"))))

(def relay-app-unreachable-message
  "We couldn't reach your Storrito Relay App. Please open the Relay App on your device, make sure the device is online, and try again.")

(def relay-app-unreachable-error-class
  "Synthetic `:error-class` of a story-post fail row (like `no-connection`
   and `missed-posting-window`) for an attempt that failed on the transport
   to the customer's Relay App: the device is offline, not the platform."
  "relay-app-unreachable")

(def relay-app-transport-error-classes
  "Exception classes a story post ends with when the Relay App on the
   customer's device does not answer: every request fails with a
   NetworkException ('Proxy responded with non 200 code: 502'), the chunked
   video upload gives up with an UploadFailedException ('All retries have
   failed')."
  ["InstagramAPI\\Exception\\NetworkException"
   "InstagramAPI\\Exception\\UploadFailedException"])

(defn relay-app-unreachable?
  "Did the attempt `result` fail on the transport to the Relay App? True
   when the posting proxy is the Relay App and the exception is one of
   `relay-app-transport-error-classes`."
  [result proxy-url]
  (boolean
   (and (relay-app-proxy? proxy-url)
        (some (fn [class-name]
                (str/includes? (str (:class result)) class-name))
              relay-app-transport-error-classes))))

(def proxy-server-unreachable-message
  "We couldn't reach this proxy server. Please check the proxy URL, make sure the proxy is online, and try again.")

(def proxy-connection-unreachable-message
  "We couldn't reach the proxy connection. Please try again in a few minutes. If it keeps happening, contact support.")

(defn proxy-access-denied-message
  "Message for a custom proxy that answered CONNECT with 403/407. Providers
   like IPRoyal return these when the credentials are wrong or when the proxy
   only allows whitelisted IPs — which works from the customer's own device
   but not from Storrito's servers."
  [status]
  (str "Your proxy server refused the connection (HTTP " status "). "
       "Please check the proxy username and password. If your proxy uses IP "
       "whitelisting, switch it to username/password authentication — "
       "Storrito connects from different IP addresses than your own devices."))

(defn result-message
  [result]
  (or (:message result)
      (:error-message result)
      (some-> result :exception str)))

(def proxy-connect-error-patterns
  "Regexes capturing the HTTP status a proxy answered instead of tunneling
   the request. curl words it 'Received HTTP code 403 from proxy after
   CONNECT ...', the fingerprint transport (mproxy/libigproxy) words it
   'Proxy responded with non 200 code: 502'."
  [#"Received HTTP code (\d{3}) from proxy after CONNECT"
   #"Proxy responded with non 200 code: (\d{3})"])

(defn proxy-connect-error-status
  "Returns the HTTP status code the proxy answered to the CONNECT request,
   parsed from curl or fingerprint-transport messages (see
   `proxy-connect-error-patterns`). Returns nil when the message is not a
   proxy CONNECT error."
  [message]
  (when (string? message)
    (or (some->> proxy-connect-error-patterns
                 (some #(re-find % message))
                 (second)
                 (#?(:clj Long/parseLong
                     :cljs js/parseInt)))
        ;; Loose fallback for reworded curl/Guzzle messages:
        (when (and (str/includes? message "502")
                   (str/includes? message "proxy")
                   (str/includes? message "CONNECT"))
          502))))

(defn friendly-error-message-short
  [result proxy-url]
  (let [message (result-message result)
        status (proxy-connect-error-status message)]
    (when (or status
              (relay-app-unreachable? result proxy-url))
      (cond
        (relay-app-proxy? proxy-url)
        relay-app-unreachable-message

        (and (seq proxy-url)
             (contains? #{403 407} status))
        (proxy-access-denied-message status)

        (seq proxy-url)
        proxy-server-unreachable-message

        :else
        proxy-connection-unreachable-message))))

(defn add-friendly-error-message-short
  [result proxy-url]
  (if-let [message (and (not (:error-message-short result))
                        (friendly-error-message-short result proxy-url))]
    (assoc result :error-message-short message)
    result))
